import Holidays from "date-holidays";
import { getWorkableHolidays } from "../api/holidayApi";
import { majorHolidays } from "../constants/constants";

export const getPayPeriodsForYear = (year) => {
  // Early date found from old access data.
  const seed = new Date("4/1/2017");

  const payPeriods = [];

  // Need to calculate dates after seed
  let currentDate = seed;

  if (seed.getFullYear() > year) {
    while (currentDate.getFullYear() >= year) {
      currentDate.setDate(currentDate.getDate() - 14);
    }
  } else {
    while (currentDate.getFullYear() < year) {
      currentDate.setDate(14 + currentDate.getDate());
    }
  }

  while (currentDate.getFullYear() <= year) {
    const startDate = new Date(currentDate);

    currentDate.setDate(14 + currentDate.getDate());
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() - 1);
    payPeriods.push({ start: startDate, end: endDate });
  }

  return payPeriods;
};

export const getLastPayPeriodForDate = (date) => {
  const _date = new Date(date);
  var currentYearPayPeriods = getPayPeriodsForYear(_date.getFullYear());
  var lastYearPayPeriods = getPayPeriodsForYear(_date.getFullYear() - 1);

  const payPeriodIndex = currentYearPayPeriods.findIndex(
    (x) => x.start <= date && x.end >= date,
  );

  let payPeriod = null;
  if (payPeriodIndex === 0 || payPeriodIndex === -1) {
    // Date is in first pay period of year, set pay period to last of previous year.
    payPeriod = lastYearPayPeriods.pop();
  } else if (payPeriodIndex > -1) {
    // Set previous pay period for this year if not first of year
    payPeriod = currentYearPayPeriods[payPeriodIndex - 1];
  } else {
    throw new Error(
      "There was a problem finding the previous pay period for today's date.",
    );
  }

  return payPeriod
    ? {
        label: `${payPeriod.start.toLocaleDateString()} - ${payPeriod.end.toLocaleDateString()}`,
        value: payPeriod,
      }
    : null;
};

export const dateSorter = (a, b) => {
  return new Date(a).getTime() - new Date(b).getTime();
};

const stdTimezoneOffset = (date) => {
  var jan = new Date(date.getFullYear(), 0, 1);
  var jul = new Date(date.getFullYear(), 6, 1);
  return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
};
export const isDstObserved = (date) => {
  return date.getTimezoneOffset() < stdTimezoneOffset(date);
};

export const isWeekday = (date) => {
  const day = new Date(date).getDay();
  return day !== 0 && day !== 6;
};

export const isLastWeekendOfMonth = (date) => {
  const dateToCheck = new Date(date);

  // Setting day to 0 means get last day of previous month
  const lastDayOfMonth = new Date(
    dateToCheck.getFullYear(),
    dateToCheck.getMonth() + 1,
    0,
  );

  const dateToCheckDay = dateToCheck.getDay();
  const dateToCheckDate = dateToCheck.getDate();

  const lastDayOfMonthDate = lastDayOfMonth.getDate();

  const isSaturday = dateToCheckDay === 6;
  const isSunday = dateToCheckDay === 0;
  const isFirstDay = dateToCheckDate === 1;
  const isLastDay = dateToCheckDate === lastDayOfMonthDate;
  const isSecondToLastDay = dateToCheckDate === lastDayOfMonthDate - 1;

  if (isSunday && isFirstDay) {
    return true;
  }
  if (isSunday && isLastDay) {
    return true;
  }
  if (isSaturday && isLastDay) {
    return true;
  }
  if (isSaturday && isSecondToLastDay) {
    return true;
  }

  return false;
};

export const isLastDayOfMonth = (date) => {
  const dateToCheck = new Date(date);

  // Setting day to 0 means get last day of previous month
  const lastDayOfMonth = new Date(
    dateToCheck.getFullYear(),
    dateToCheck.getMonth() + 1,
    0,
  );

  const dateToCheckDate = dateToCheck.getDate();

  const lastDayOfMonthDate = lastDayOfMonth.getDate();

  return dateToCheckDate === lastDayOfMonthDate;
};

export const isHoliday = async (date) => {
  const hd = new Holidays("US", "MN");
  const currentDate = new Date(date);
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  let allHolidays = [];
  let publicHolidays = [];

  const [workableHolidaysCurrentYear, workableHolidaysNextYear] = await Promise.all([
    getWorkableHolidays(currentYear),
    getWorkableHolidays(nextYear)
  ]);


  const currentYearHolidays = hd.getHolidays(String(currentYear)).filter(h => {
    return !workableHolidaysCurrentYear.some(c => c.holidayName === h.name)
  });
  allHolidays = allHolidays.concat(currentYearHolidays);
  const nextYearHolidays = hd.getHolidays(String(nextYear)).filter(h => {
    return !workableHolidaysNextYear.some(c => c.holidayName === h.name)
  })
  allHolidays = allHolidays.concat(nextYearHolidays);
  for (const holiday of allHolidays) {
    if (majorHolidays.includes(holiday.name)) {
      publicHolidays = publicHolidays.concat(new Date(holiday.date).toDateString());
    }
  }

  return publicHolidays.includes(currentDate.toDateString());
};

export const getXBusinessDaysFromStartDate = async (startDate, numberOfDays) => {
  let workDates = [];

  let currentDate = new Date(startDate);
  for (let i = 0; i < numberOfDays; i++) {
    let validDate = false;
    while (!validDate) {
      currentDate.setDate(currentDate.getDate() + 1);

      if (
        (isWeekday(currentDate) || isLastWeekendOfMonth(currentDate)) &&
        !(await isHoliday(currentDate))
      ) {
        validDate = true;
      }
    }

    currentDate.setHours(0, 0, 0, 0);
    workDates.push(currentDate.toISOString());
  }

  return workDates;
};

export const getXBusinessDaysFromStartDateForCustomer = async (
  startDate,
  numberOfDays,
) => {
  let workDates = [];

  let currentDate = new Date(startDate);
  for (let i = 0; i < numberOfDays; i++) {
    let validDate = false;
    while (!validDate) {
      currentDate.setDate(currentDate.getDate() + 1);

      if (
        isWeekday(currentDate) &&
        !isLastDayOfMonth(currentDate) &&
        !(await isHoliday(currentDate))
      ) {
        validDate = true;
      }
    }

    currentDate.setHours(0, 0, 0, 0);
    workDates.push(currentDate.toISOString());
  }

  return workDates;
};

export const getMinDate = (isCustomer) => {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const day = today.getUTCDate();
  const hours = today.getUTCHours();

  const customerOffset = isCustomer ? 1 : 0;

  if (isDstObserved(today)) {
    if (hours >= 20) {
      return new Date(year, month, day + 1 + customerOffset);
    } else {
      return new Date(year, month, day + customerOffset);
    }
  } else {
    if (hours >= 21) {
      return new Date(year, month, day + 1 + customerOffset);
    } else {
      return new Date(year, month, day + customerOffset);
    }
  }
};

export const getCustStartDate = () => {
  return new Date(2022, 10, 8);
};

export const getCustInvoiceStartDate = () => {
  return new Date(2022, 1, 3);
};

export const isAfterToday = (date) => {
  const _date = new Date(date);
  const today = new Date();

  _date.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return _date > today;
};

export const getMaxDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();
  return new Date(year, month + 6, day);
};

export const formatTime = (date) => {
  if (date != null) {
    const formattedDate = new Date(date)
      ?.toTimeString()
      .split(" ")
      .slice(1)
      .join(" ");
    return formattedDate;
  } else {
    return "";
  }
};

export const formatDate = (date) => {
  if (date != null) {
    const formattedDate = new Date(date)
      ?.toDateString()
      .split(" ")
      .slice(1)
      .join(" ");
    return formattedDate;
  } else {
    return "";
  }
};

export const convertToLocaleString = (date) => {
  if (!date) {
    return null;
  }

  const offset = date.getTimezoneOffset() * 60 * 1000;
  let tLocal = date - offset;
  tLocal = new Date(tLocal);
  let iso = tLocal.toISOString();
  iso = iso.slice(0, 19);
  iso = iso.replace("T", " ");

  return iso;
};

export const getMonthByNumber = (monthNumber) => {
  switch (monthNumber) {
    case 1:
      return "January";
    case 2:
      return "February";
    case 3:
      return "March";
    case 4:
      return "April";
    case 5:
      return "May";
    case 6:
      return "June";
    case 7:
      return "July";
    case 8:
      return "August";
    case 9:
      return "September";
    case 10:
      return "October";
    case 11:
      return "November";
    case 12:
      return "December";
    default:
      return;
  }
};
